/**
 * Controlled DocHub V2 document state helpers.
 * Paths use template | segments: additionalFields|tradeMake → ['additionalFields','tradeMake']
 * inventoryUnit|* is NOT stored in documentState — it lives on the picked inventory object.
 */

import { normalizeRefId } from './resolveVariableField';
import {
  CUSTOM_TABLE_FIELDS,
  normalizeCustomTableField
} from './customSheetFields';

/** Keep arrays/plain objects as-is; only coerce populated Mongo refs to id strings. */
export const normalizeDocumentParamValue = value => {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value;
  if (typeof value === 'object' && value._id != null) return normalizeRefId(value);
  if (typeof value === 'object') return value;
  return value;
};

export const parseTokenPath = fullToken =>
  String(fullToken || '')
    .split('|')
    .map(part => part.trim())
    .filter(Boolean);

export const getValueAtPath = (obj, path = []) => {
  if (!path.length) return obj;
  let cur = obj;
  for (const key of path) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[key];
  }
  return cur;
};

/** Immutable set; creates plain objects along the path as needed. */
export const setValueAtPath = (obj, path = [], value) => {
  if (!path.length) return value;
  const root = obj && typeof obj === 'object' && !Array.isArray(obj) ? { ...obj } : {};
  let cur = root;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    const next = cur[key];
    cur[key] =
      next && typeof next === 'object' && !Array.isArray(next) ? { ...next } : {};
    cur = cur[key];
  }
  cur[path[path.length - 1]] = value;
  return root;
};

export const isInventoryPath = path => path?.[0] === 'inventoryUnit';

export const isAdditionalFieldsPath = path => path?.[0] === 'additionalFields';

export const isCustomEntitiesPath = path => {
  if (!path?.length) return false;
  if (path[0] === 'custom' || path[0] === 'customEntities') return true;
  return path[0] === 'inventoryUnit' && (path[1] === 'custom' || path[1] === 'customEntities');
};

export const getCustomEntityPathParts = path => {
  if (path?.[0] === 'inventoryUnit' && (path[1] === 'custom' || path[1] === 'customEntities')) {
    return { entityName: path[2] || '', displayLeaf: path[3] || null, inventoryBound: true };
  }
  if (path?.[0] === 'custom' || path?.[0] === 'customEntities') {
    return { entityName: path[1] || '', displayLeaf: path[2] || null, inventoryBound: false };
  }
  return null;
};

/** Display/edit primitives only; objects stay for later (nested pickers, etc.). */
export const toInputValue = value => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return '';
  return String(value);
};

/**
 * Build documentState from foundParams split ({ withData, withoutData }).
 * - Skips inventoryUnit (handled via inventory meta)
 * - additionalFields: only keys referenced by this document (merge on save later)
 */
export const initDocumentStateFromParams = documentFields => {
  const params = [...(documentFields?.withData || []), ...(documentFields?.withoutData || [])];
  const state = { additionalFields: {}, customEntities: {} };

  params.forEach(param => {
    // Backend sometimes pushes bare strings into withoutData for sheet-name fields.
    const name = typeof param === 'string' ? param : param?.name;
    if (!name || name === 'inventoryUnit') return;

    // Printable {{ custom | Entity | column }} blob — IDs live on customEntities.
    if (name === 'custom') return;

    if (name === 'additionalFields') {
      const src = param.value && typeof param.value === 'object' ? param.value : {};
      const keys = (param.pathsInDoc || []).map(path => path[path.length - 1]).filter(Boolean);
      if (keys.length) {
        keys.forEach(key => {
          const v = src[key];
          state.additionalFields[key] = normalizeDocumentParamValue(v);
        });
      } else if (param.value && typeof param.value === 'object') {
        Object.keys(src).forEach(key => {
          const v = src[key];
          state.additionalFields[key] = normalizeDocumentParamValue(v);
        });
      }
      return;
    }

    if (name === 'customEntities') {
      const src =
        param.value && typeof param.value === 'object' && !Array.isArray(param.value) ? param.value : {};
      Object.keys(src).forEach(key => {
        const v = src[key];
        if (v === undefined || v === null || v === '') return;
        state.customEntities[String(key)] = typeof v === 'object' && v._id != null ? String(v._id) : String(v);
      });
      return;
    }

    const raw = typeof param === 'string' ? '' : param.value ?? '';
    if (CUSTOM_TABLE_FIELDS.has(name)) {
      state[name] = normalizeCustomTableField(name, raw);
      return;
    }

    state[name] = normalizeDocumentParamValue(raw);
  });

  return state;
};

/**
 * Inventory meta from foundParams — same rules as V1 DocumentPrinter.handleInventoryUnit.
 */
export const initInventoryMetaFromParams = (documentFields, docSource) => {
  const withData = [...(documentFields?.withData || [])];
  const withoutData = [...(documentFields?.withoutData || [])];

  const withoutIdx = withoutData.findIndex(f => f.name === 'inventoryUnit');
  if (withoutIdx !== -1) {
    return ['Contract', 'Inventory'].includes(docSource) ? {} : { inventoryUnits: [] };
  }

  const withIdx = withData.findIndex(f => f.name === 'inventoryUnit');
  if (withIdx !== -1) {
    const value = withData[withIdx].value;
    if (['Contract', 'Inventory'].includes(docSource)) {
      return { pickedInventory: value || undefined };
    }
    const units = Array.isArray(value) ? value : value ? [value] : [];
    return {
      inventoryUnits: units,
      pickedInventory: units[0]
    };
  }

  return null; // document does not reference inventoryUnit
};

export const readVariableValue = ({ path, documentState, pickedInventory }) => {
  if (!path?.length) return '';
  if (isInventoryPath(path)) {
    return getValueAtPath(pickedInventory, path.slice(1));
  }
  return getValueAtPath(documentState, path);
};
